// The left column of a section that lives in pipelines (R-U29, decision 040 п.3–4, 6): «Мои» → «Все» with its filter branches →
// the pipelines, each a page of its own with its open records counted → «Новая воронка / категория» for the owner.

import { useState } from 'react';
import { uuidv7, type FieldDef, type Filter, type Id, accountToday } from '@confdeck/core-shared';
import { useMeta, useQuery, useSync } from '../sync/react.tsx';
import { navigate, typeHref } from '../shell/router.ts';
import { Branches, NavRow, navCss } from '../shell/SideNav.tsx';
import { listHref } from '../shell/nav-terms.ts';
import { useToast } from '../ui/primitives.tsx';
import { branchFilter, mineFilter, mineHref, openFilter, pipelineField, pipelineHref, pipelinesOf, type PipelineSectionDef } from './pipeline-section.ts';
import s from './pipelines.module.css';

/** What of the section is the open page: «Мои», «Все», a branch of «Все» or a pipeline (the shell's lit item). */
export type PipelineLit = { kind: 'mine' } | { kind: 'all' } | { kind: 'branch'; key: string } | { kind: 'pipeline'; id: Id } | null;

/** The lit item as the shell keeps it (`NavActive.extra`): `mine`, `branch:<key>` or a pipeline's id; the section itself is «Все». */
export function pipelineLit(extra: string | undefined, typeLit: boolean): PipelineLit {
  if (extra === 'mine') return { kind: 'mine' };
  if (extra?.startsWith('branch:')) return { kind: 'branch', key: extra.slice(7) };
  if (extra) return { kind: 'pipeline', id: extra };
  return typeLit ? { kind: 'all' } : null;
}

export function PipelineSectionNav({ typeId, def, lit }: { typeId: Id; def: PipelineSectionDef; lit: PipelineLit }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [adding, setAdding] = useState<string | null>(null);
  const t = meta.type(typeId);
  const field = pipelineField(meta, typeId);
  if (!t || !field) return null;
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  const today = accountToday(meta);
  const create = async (name: string) => {
    const id = uuidv7();
    setAdding(null);
    const r = await client.run('pipeline.create', { id, taxonomyId: field.options.taxonomyId, name });
    if (!r.ok) { toast({ kind: 'error', text: r.error.message }); return; }
    navigate(pipelineHref(id, 'settings'));
  };
  return (
    <>
      <NavRow icon="mine" label={def.words.mine} on={lit?.kind === 'mine'} onClick={() => navigate(mineHref(t.key))}
        count={<FilterCount typeId={typeId} filter={mineFilter(meta, def, field, client.header?.userId)} />} />
      <div className={navCss.navNode}>
        <NavRow icon={t.icon} label={def.words.all} on={lit?.kind === 'all'} onClick={() => navigate(typeHref(t.key))}
          count={<span className={navCss.navCount}>{client.store.count(typeId).toLocaleString('ru-RU')}</span>} />
        {!!def.branches?.length && (
          <Branches>
            {def.branches.map((b) => (
              <NavRow key={b.key} title={b.label} label={<BranchLabel field={field} kind={b.kind} label={b.label} />} on={lit?.kind === 'branch' && lit.key === b.key}
                onClick={() => navigate(listHref(t.key, null, null, b.key))} count={<FilterCount typeId={typeId} filter={branchFilter(meta, def, field, b.kind, today)} />} />
            ))}
          </Branches>
        )}
      </div>
      {pipelinesOf(meta, field).map((p) => (
        <NavRow key={p.id} dot={p.color} label={p.name} title={p.name} on={lit?.kind === 'pipeline' && lit.id === p.id} onClick={() => navigate(pipelineHref(p.id))}
          count={<FilterCount typeId={typeId} filter={openFilter(meta, field, p.id)} />}
          action={isOwner ? { icon: 'settings', label: `Настроить ${def.words.pipelineAcc} «${p.name}»`, onClick: () => navigate(pipelineHref(p.id, 'settings')) } : undefined} />
      ))}
      {isOwner && (adding === null ? (
        <NavRow icon="plus" label={def.words.create} muted onClick={() => setAdding('')} />
      ) : (
        <input autoFocus className={s.input} placeholder="Название" aria-label={def.words.create} value={adding} onChange={(e) => setAdding(e.target.value)} onBlur={() => setAdding(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setAdding(null); if (e.key === 'Enter' && adding.trim()) void create(adding.trim()); }} />
      ))}
    </>
  );
}

/** A branch by stage carries the colour of its stages (of the first pipeline); the others are plain words. */
function BranchLabel({ field, kind, label }: { field: FieldDef; kind: string; label: string }) {
  const meta = useMeta();
  const first = pipelinesOf(meta, field)[0];
  const stages = first ? meta.children(first.id, first.taxonomyId) : [];
  const open = stages.filter((x) => !x.final);
  const stage = kind === 'start' ? open[0] : kind === 'work' ? open[1] : kind === 'won' || kind === 'lost' ? stages.find((x) => x.final === kind) : undefined;
  return <>{stage && <span className={navCss.termDot} style={{ background: `var(--c-${stage.color}-dot)` }} />}{label}</>;
}

function FilterCount({ typeId, filter }: { typeId: Id; filter: Filter | null }) {
  const q = useQuery(typeId, filter, [], '');
  return <span className={navCss.navCount}>{q.ids.length.toLocaleString('ru-RU')}</span>;
}
